<script setup lang="ts">
import { computed, onMounted, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import type OverlayPanel from 'primevue/overlaypanel';
import { useAToast } from '@Atoms';
import { useHelpState, useHelpTipTexts, useHelpVisibility, useOpenedTips } from '../../../composables';
import { HELP_STATE_ACTIONS, HELP_STATE_TYPES } from '../../../enums';
import useWhatsNewStore from '../../../store';

const props = defineProps<{
  // A small pulsing circle beside the control instead of the New pill on its corner, for menu links.
  isDot?: boolean;
  loading?: boolean;
  tipKey: string;
}>();

const { isReady } = storeToRefs(useWhatsNewStore());

const { notify, TOAST_TYPES } = useAToast();
const { isSaving, saveHelpState } = useHelpState();
const { getTipText, getTipTitle } = useHelpTipTexts();
const { checkTipActive, checkTipHidden, findReadableArticle, findTip, loadEntries } = useHelpVisibility();
const { markTipOpened, wasTipOpened } = useOpenedTips();
const { push } = useRouter();

const TOAST_DISMISSED = {
  message: 'helpTips.toasts.dismissed.message',
  title: 'helpTips.toasts.dismissed.title',
  type: TOAST_TYPES.SUCCESS,
};

const popover = useTemplateRef<InstanceType<typeof OverlayPanel>>('popover');

const tip = computed(() => findTip(props.tipKey));
const article = computed(() => findReadableArticle(tip.value?.articleKey));
const isActive = computed(() => !props.loading && isReady.value && checkTipActive(tip.value));
// NOTE :: Only a super admin gets here for a hidden feature: `checkTipActive` drops it for everyone else.
const isHidden = computed(() => checkTipHidden(tip.value));
const isPulsing = computed(() => !wasTipOpened(props.tipKey));

onMounted(onComponentMount);

// EVENTS
function onComponentMount() {
  loadEntries();
}

function onToggle(event: Event) {
  popover.value?.toggle(event);
  markTipOpened(props.tipKey);
}

function onDismiss() {
  const ITEM = { action: HELP_STATE_ACTIONS.DISMISSED, key: props.tipKey, type: HELP_STATE_TYPES.ANNOUNCEMENT };

  saveHelpState(ITEM).then((wasSaved) => {
    if (!wasSaved) return;

    popover.value?.hide();
    notify(TOAST_DISMISSED);
  });
}

function onSeeMore() {
  if (!article.value) return;

  popover.value?.hide();
  push({ name: 'support-whats-new-article', params: { key: article.value.key } });
}
</script>

<template>
  <span class="relative inline-flex">
    <slot></slot>

    <template v-if="isActive">
      <button
        type="button"
        :aria-label="getTipTitle(tipKey)"
        :class="['z-10 cursor-pointer', isDot ? 'ml-1.5 inline-flex items-center' : 'absolute -right-2 -top-4']"
        @click="onToggle"
      >
        <ANewPill
          :isDot
          :isPulsing
        />
      </button>

      <OverlayPanel ref="popover">
        <div class="flex max-w-72 flex-col gap-2">
          <p
            v-if="isHidden"
            class="flex items-start gap-1.5 text-xs text-slate-500"
          >
            <ANewPill
              isHidden
              class="mt-px"
            />

            {{ $t('helpTips.popover.hiddenNote') }}
          </p>

          <p class="text-sm font-semibold text-slate-700">
            {{ getTipTitle(tipKey) }}
          </p>

          <p class="text-sm text-slate-600">
            {{ getTipText(tipKey) }}
          </p>

          <div class="mt-1 flex flex-wrap gap-2">
            <Button
              class="af-button"
              severity="secondary"
              :label="$t('helpTips.popover.dismiss')"
              :loading="isSaving"
              @click="onDismiss"
            />

            <Button
              v-if="article"
              outlined
              class="af-button af-button-success-outlined"
              :label="$t('helpTips.popover.seeMore')"
              @click="onSeeMore"
            />
          </div>
        </div>
      </OverlayPanel>
    </template>
  </span>
</template>
